import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Draait dit tabblad nog de code die de server nú serveert?
 *
 * **Waarom dit bestaat.** Na een uitrol blijft een tabblad dat al openstond
 * de code draaien die het toen geladen heeft. De startpagina staat op
 * `no-cache`, dus één herlaadbeurt is genoeg — maar niets vertelt je dat je
 * die nodig hebt. Op 16 september 2026 leverde dat een zoektocht op naar een
 * uitrol die allang goed stond: de wisselknop in het oude tabblad had nog het
 * oude gedrag en logde je uit. Het beeld was niet te onderscheiden van een
 * kapotte uitrol.
 *
 * **Waarom niet het versienummer.** `/api/version` bestaat, maar dat nummer
 * verandert alleen bij een release. Tussen twee releases zitten uitrollen
 * waarbij het nummer gelijk blijft en de code wél wijzigt — precies het geval
 * hierboven, waarin staging en productie allebei 1.13.26 heetten.
 *
 * **Wat wel verandert bij elke bouw:** de bestandsnaam van de hoofdbundel.
 * Die draagt een afdruk van de inhoud (`index-D9K9sVQN.js`). We vergelijken
 * dus de bundel waarmee deze pagina is opgestart met de bundel waar de
 * startpagina op de server nú naar verwijst. Geen backend-wijziging voor
 * nodig, en het merkt élke uitrol op, niet alleen een release.
 *
 * **Nooit vanzelf herladen.** Iemand kan midden in een pagina zitten te
 * schrijven. Dit meldt alleen; herladen is een keuze.
 */

/** Ruim: een gemiste uitrol is hooguit hinderlijk, geen storing. */
const INTERVAL_MS = 15 * 60 * 1000

export const useAppVersionStore = defineStore('appVersion', () => {
  /** Er staat een nieuwere versie klaar op de server. */
  const nieuweVersie = ref(false)

  /** Weggeklikt: niet meer tonen tot de volgende paginalading. */
  const weggeklikt = ref(false)

  let timer: number | undefined
  let eigenBundel: string | null = null
  let luisterend = false

  /**
   * De hoofdbundel van een document. Dezelfde selector voor de eigen pagina
   * en voor het opgehaalde antwoord, zodat er appels met appels vergeleken
   * worden en een verschil in vorm nooit als nieuwe versie telt.
   */
  function hoofdbundel(doc: Document): string | null {
    const script = doc.querySelector<HTMLScriptElement>(
      'script[type="module"][src*="/assets/"]'
    )

    if (!script) {
      return null
    }

    // getAttribute en niet .src: dat laatste maakt er in het opgehaalde
    // document een absolute URL van tegen een andere basis.
    return script.getAttribute('src')
  }

  async function bundelOpDeServer(): Promise<string | null> {
    // no-store: we willen weten wat de server nú serveert, niet wat deze
    // browser ooit heeft opgeslagen.
    const antwoord = await fetch('/index.html', {
      cache: 'no-store',
      headers: { Accept: 'text/html' },
    })

    if (!antwoord.ok) {
      return null
    }

    const html = await antwoord.text()

    // DOMParser voert niets uit; het is alleen lezen. Een regex op HTML zou
    // hier ook een modulepreload-verwijzing kunnen vangen, en dan meldt hij
    // een nieuwe versie die er niet is.
    return hoofdbundel(new DOMParser().parseFromString(html, 'text/html'))
  }

  async function controleer(): Promise<void> {
    if (nieuweVersie.value || eigenBundel === null) {
      return
    }

    try {
      const opDeServer = await bundelOpDeServer()

      // Niets gevonden: dan weten we het niet, en dat is geen nieuws.
      // Stil blijven is hier de juiste uitkomst — een valse melding kost
      // vertrouwen dat deze balk juist nodig heeft.
      if (opDeServer === null || opDeServer === eigenBundel) {
        return
      }

      nieuweVersie.value = true
      stop()
    } catch {
      // Netwerk even weg, of offline. Volgende ronde opnieuw.
    }
  }

  function bijZichtbaarWorden(): void {
    if (document.visibilityState === 'visible') {
      void controleer()
    }
  }

  /**
   * Starten heeft alleen zin met een gebouwde bundel om mee te vergelijken.
   *
   * Een aparte controle op de ontwikkelstand is daar niet voor nodig, en zat
   * er eerst wél in: de selector eist `/assets/`, en in ontwikkeling laadt
   * Vite `/src/main.ts`. Dan is er dus vanzelf niets te vergelijken. Die
   * dubbele rem was bovendien schadelijk — in de tests stond de app in
   * ontwikkelstand, waardoor vijf tests slaagden omdat er niets gebeurde in
   * plaats van omdat het klopte.
   */
  function start(): void {
    if (timer !== undefined) {
      return
    }

    eigenBundel = hoofdbundel(document)

    if (eigenBundel === null) {
      return
    }

    timer = window.setInterval(() => void controleer(), INTERVAL_MS)

    if (!luisterend) {
      // De belangrijkste trigger: je komt terug bij een tabblad dat je
      // gisteren hebt laten staan. Wachten op de volgende ronde zou juist
      // dan het langst duren.
      document.addEventListener('visibilitychange', bijZichtbaarWorden)
      luisterend = true
    }
  }

  function stop(): void {
    if (timer !== undefined) {
      window.clearInterval(timer)
      timer = undefined
    }

    if (luisterend) {
      document.removeEventListener('visibilitychange', bijZichtbaarWorden)
      luisterend = false
    }
  }

  function negeer(): void {
    weggeklikt.value = true
  }

  function herlaad(): void {
    window.location.reload()
  }

  return {
    nieuweVersie,
    weggeklikt,
    start,
    stop,
    controleer,
    negeer,
    herlaad,
  }
})
